import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../../../molecules/Card';
import { venueAdminService } from '../../../../services/venueAdminService';
import { emitVenueAdminVerified } from '../../../../utils/venueAdminEvents';

function SelectAllCheckbox({ checked, indeterminate, onChange, disabled, ariaLabel }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);
    return (
        <input
            ref={ref}
            type="checkbox"
            className="system-health-page__row-checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            aria-label={ariaLabel}
        />
    );
}

const REVIEW_PAGE_SIZE = 50;
const REVIEW_PAGE_LOOKAHEAD = REVIEW_PAGE_SIZE + 1;

const REASON_LABELS = {
    music_category_match: 'Music match',
    has_website: 'Website',
    has_phone: 'Phone',
    has_address: 'Address',
    recently_refreshed: 'Refreshed',
    manual_review_required: 'Manual review',
};

function getErrorMessage(error) {
    return error?.body?.message || error?.message || 'Unable to load venue review queue';
}

function formatLocation(location = {}) {
    return [
        location.addressLine,
        location.city,
        location.stateOrRegion,
        location.countryCode || location.country,
    ].filter(Boolean).join(', ');
}

function humanizeReason(reason) {
    if (!reason) {
        return '';
    }
    if (REASON_LABELS[reason]) {
        return REASON_LABELS[reason];
    }
    return reason
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function humanizeStatus(status) {
    if (!status) {
        return 'Unknown';
    }
    return String(status)
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function statusVariant(status) {
    if (!status) {
        return 'neutral';
    }
    const upper = String(status).toUpperCase();
    if (upper.includes('REJECT') || upper.includes('FAIL')) {
        return 'danger';
    }
    if (upper.includes('REVIEW') || upper.includes('MANUAL') || upper.includes('PENDING')) {
        return 'warning';
    }
    if (upper.includes('PUBLISHED') || upper.includes('AUTO') || upper.includes('VERIFIED')) {
        return 'success';
    }
    return 'neutral';
}

function confidenceVariant(value) {
    if (!Number.isFinite(value)) {
        return 'neutral';
    }
    if (value >= 90) return 'success';
    if (value >= 75) return 'good';
    if (value >= 60) return 'warning';
    return 'danger';
}

function clampPercent(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
}

function mergeReviewItems(currentItems, nextItems) {
    const seen = new Set();
    return [...currentItems, ...nextItems].filter((item) => {
        if (!item?.id || seen.has(item.id)) {
            return false;
        }
        seen.add(item.id);
        return true;
    });
}

export default function VenueReviewPanel() {
    const [items, setItems] = useState([]);
    const itemsRef = useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [verifyingId, setVerifyingId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [isBatchVerifying, setIsBatchVerifying] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

    const pendingCount = useMemo(() => items.length, [items]);
    const pendingLabel = useMemo(() => {
        if (pendingCount === 1) {
            return hasMore ? '1+ venues pending review' : '1 venue pending review';
        }
        return hasMore
            ? `${pendingCount}+ venues pending review`
            : `${pendingCount} venues pending review`;
    }, [hasMore, pendingCount]);
    const selectedCount = selectedIds.size;
    const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
    const someSelected = selectedCount > 0 && !allSelected;
    const isBusy = Boolean(verifyingId) || isBatchVerifying;

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    const fetchPage = useCallback(async ({ append, offset }) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }
        setError(null);
        setActionError(null);
        try {
            const data = await venueAdminService.fetchVenueReviewItems({
                limit: REVIEW_PAGE_LOOKAHEAD,
                offset,
            });
            const fetchedItems = Array.isArray(data) ? data : [];
            const pageItems = fetchedItems.slice(0, REVIEW_PAGE_SIZE);
            const currentItems = itemsRef.current;
            const nextItems = append ? mergeReviewItems(currentItems, pageItems) : pageItems;
            const advanced = !append || nextItems.length > currentItems.length || fetchedItems.length === 0;
            setHasMore(advanced && fetchedItems.length > REVIEW_PAGE_SIZE);
            setItems(nextItems);
            itemsRef.current = nextItems;
            if (!advanced) {
                setActionError('Unable to advance the review queue. Verify loaded venues or refresh before loading more.');
            }
            if (!append) {
                setSelectedIds(new Set());
            }
        } catch (loadError) {
            setError(getErrorMessage(loadError));
            if (!append) {
                setItems([]);
                setSelectedIds(new Set());
                setHasMore(false);
            }
        } finally {
            if (append) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    }, []);

    const loadItems = useCallback(async () => {
        await fetchPage({ append: false, offset: 0 });
    }, [fetchPage]);

    const loadMoreItems = useCallback(async () => {
        await fetchPage({ append: true, offset: items.length });
    }, [fetchPage, items.length]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const toggleSelect = useCallback((id) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((current) => {
            if (items.length > 0 && items.every((item) => current.has(item.id))) {
                return new Set();
            }
            return new Set(items.map((item) => item.id));
        });
    }, [items]);

    const handleVerify = useCallback(async (item) => {
        if (!item?.id || isBusy) {
            return;
        }
        setVerifyingId(item.id);
        setActionError(null);
        try {
            const verifiedVenue = await venueAdminService.verifyVenueReviewItem(item.id);
            setItems((current) => current.filter((candidate) => candidate.id !== item.id));
            setSelectedIds((current) => {
                if (!current.has(item.id)) return current;
                const next = new Set(current);
                next.delete(item.id);
                return next;
            });
            emitVenueAdminVerified({
                venueId: item.id,
                venue: verifiedVenue,
            });
        } catch (verifyError) {
            setActionError(getErrorMessage(verifyError));
        } finally {
            setVerifyingId(null);
        }
    }, [isBusy]);

    const handleVerifySelected = useCallback(async () => {
        if (isBusy) {
            return;
        }
        const ids = items.map((item) => item.id).filter((id) => selectedIds.has(id));
        if (ids.length === 0) {
            return;
        }
        setIsBatchVerifying(true);
        setActionError(null);
        setBatchProgress({ current: 0, total: ids.length });
        let failed = 0;
        let lastError = null;
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            setBatchProgress({ current: i + 1, total: ids.length });
            try {
                const verifiedVenue = await venueAdminService.verifyVenueReviewItem(id);
                setItems((current) => current.filter((candidate) => candidate.id !== id));
                setSelectedIds((current) => {
                    if (!current.has(id)) return current;
                    const next = new Set(current);
                    next.delete(id);
                    return next;
                });
                emitVenueAdminVerified({ venueId: id, venue: verifiedVenue });
            } catch (verifyError) {
                failed += 1;
                lastError = verifyError;
            }
        }
        if (failed > 0) {
            setActionError(`Failed to verify ${failed} of ${ids.length} venues. ${getErrorMessage(lastError)}`);
        }
        setBatchProgress({ current: 0, total: 0 });
        setIsBatchVerifying(false);
    }, [items, selectedIds, isBusy]);

    return (
        <Card
            className="system-health-page__admin-card"
            allowOverflow
            title={(
                <div className="system-health-page__admin-card-header">
                    <div>
                        <h2>Venue Approvals</h2>
                        <p className="system-health-page__summary-meta">
                            {pendingLabel}
                            {selectedCount > 0 ? ` · ${selectedCount} selected` : ''}
                        </p>
                    </div>
                    <div className="system-health-page__admin-card-actions">
                        <button
                            type="button"
                            className="btn-primary system-health-page__admin-bulk-verify"
                            onClick={handleVerifySelected}
                            disabled={selectedCount === 0 || isBusy}
                        >
                            {isBatchVerifying
                                ? `Verifying ${batchProgress.current}/${batchProgress.total}...`
                                : `Verify Selected${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                        </button>
                        <button
                            type="button"
                            className="btn-secondary system-health-page__admin-refresh"
                            onClick={loadItems}
                            disabled={isLoading || isLoadingMore || isBusy}
                        >
                            {isLoading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>
            )}
            body={(
                <div className="system-health-page__venue-review">
                    {error ? (
                        <div className="system-health-page__alert system-health-page__alert--error">
                            {error}
                        </div>
                    ) : null}
                    {actionError ? (
                        <div className="system-health-page__alert system-health-page__alert--error">
                            {actionError}
                        </div>
                    ) : null}
                    {isLoading && items.length === 0 ? (
                        <div className="system-health-page__loader" role="status" aria-live="polite">
                            Loading venue review queue...
                        </div>
                    ) : null}
                    {!isLoading && !error && items.length === 0 && !hasMore ? (
                        <div className="system-health-page__empty">
                            <p>No venues are waiting for review.</p>
                        </div>
                    ) : null}
                    {!isLoading && !error && items.length === 0 && hasMore ? (
                        <div className="system-health-page__empty">
                            <p>Visible venues were cleared. More pending venues are available.</p>
                        </div>
                    ) : null}
                    {items.length > 0 ? (
                        <div className="system-health-page__venue-review-table-wrap">
                            <table className="system-health-page__venue-review-table">
                                <thead>
                                    <tr>
                                        <th scope="col" className="system-health-page__select-cell">
                                            <SelectAllCheckbox
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={toggleSelectAll}
                                                disabled={items.length === 0 || isBusy}
                                                ariaLabel="Select all venues"
                                            />
                                        </th>
                                        <th scope="col">Venue</th>
                                        <th scope="col">Location</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Confidence</th>
                                        <th scope="col">Reason</th>
                                        <th scope="col">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => {
                                        const statusKey = statusVariant(item.publicationStatus);
                                        const confKey = confidenceVariant(item.confidenceScore);
                                        const reasons = Array.isArray(item.autoPublishReasons)
                                            ? item.autoPublishReasons
                                            : [];
                                        const checked = selectedIds.has(item.id);
                                        return (
                                            <tr
                                                key={item.id}
                                                className={
                                                    checked
                                                        ? 'system-health-page__venue-row system-health-page__venue-row--selected'
                                                        : 'system-health-page__venue-row'
                                                }
                                            >
                                                <td className="system-health-page__select-cell">
                                                    <input
                                                        type="checkbox"
                                                        className="system-health-page__row-checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleSelect(item.id)}
                                                        disabled={isBusy}
                                                        aria-label={`Select ${item.name}`}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="system-health-page__venue-name">{item.name}</div>
                                                    <code
                                                        className="system-health-page__venue-id"
                                                        title={item.id}
                                                    >
                                                        {item.id}
                                                    </code>
                                                </td>
                                                <td>
                                                    <div className="system-health-page__venue-location">
                                                        {formatLocation(item.location)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`system-health-page__status-chip system-health-page__status-chip--${statusKey}`}
                                                    >
                                                        {humanizeStatus(item.publicationStatus)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div
                                                        className={`system-health-page__confidence system-health-page__confidence--${confKey}`}
                                                    >
                                                        <span className="system-health-page__confidence-value">
                                                            {Number.isFinite(item.confidenceScore)
                                                                ? item.confidenceScore
                                                                : '—'}
                                                        </span>
                                                        <span
                                                            className="system-health-page__confidence-bar"
                                                            aria-hidden="true"
                                                        >
                                                            <span
                                                                className="system-health-page__confidence-bar-fill"
                                                                style={{
                                                                    width: `${clampPercent(item.confidenceScore)}%`,
                                                                }}
                                                            />
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {reasons.length > 0 ? (
                                                        <ul className="system-health-page__reason-chips">
                                                            {reasons.map((reason) => (
                                                                <li
                                                                    key={reason}
                                                                    className="system-health-page__reason-chip"
                                                                    title={reason}
                                                                >
                                                                    {humanizeReason(reason)}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <span className="system-health-page__reason-empty">
                                                            No reason recorded
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="system-health-page__action-cell">
                                                    <button
                                                        type="button"
                                                        className="btn-primary system-health-page__verify-button"
                                                        onClick={() => handleVerify(item)}
                                                        disabled={isBusy}
                                                    >
                                                        {verifyingId === item.id ? 'Verifying...' : 'Verify'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                    {hasMore ? (
                        <div className="system-health-page__venue-review-pagination">
                            <span>
                                {pendingCount > 0
                                    ? `Showing ${pendingCount} pending venues`
                                    : 'More pending venues are available'}
                            </span>
                            <button
                                type="button"
                                className="btn-secondary system-health-page__load-more"
                                onClick={loadMoreItems}
                                disabled={isLoading || isLoadingMore || isBusy}
                            >
                                {isLoadingMore ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    ) : null}
                </div>
            )}
            footer={(
                <p className="system-health-page__footnote">
                    Verification publishes the canonical venue object and creates or refreshes its map projection.
                </p>
            )}
        />
    );
}
